import { ChangeDetectionStrategy, Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialogActions } from '@angular/material/dialog';
import { capitalize } from '@app/api/core/utils/string.util';
import { AbstractReactiveDialogComponent } from '@app/shared/components/dialog/reactive-dialog/base/abstract-reactive-dialog-component';
import { ReactiveDialogContentComponent } from '@app/shared/components/dialog/reactive-dialog/base/reactive-dialog-content/reactive-dialog-content.component';
import { BasicIconButtonComponent } from '@app/shared/components/form/basic-icon-button/basic-icon-button.component';
import { UploadFileComponent } from '@app/shared/components/upload-file/upload-file.component';
import { SimpleDocument } from '@app/shared/components/upload-file/upload-file.types';
import { FormatNumberPipe } from '@app/shared/pipes/formatnumber.pipe';
import { DataService } from '@app/shared/services/data/data.service';
import { MessageService } from '@app/shared/services/ui/message/message.service';
import { MaklerDocumentType, MaklerPolizzeDetails, PolizzeResult } from '@o30/entities';
import { filter, take } from 'rxjs/operators';

export interface AntragDocumentUploadDialogContext {
  maklerDocumentType: MaklerDocumentType;
  simpleDocument: SimpleDocument;
}

@Component({
  selector:        'app-antrag-document-upload-dialog',
  templateUrl:     './antrag-document-upload-dialog.component.html',
  styleUrls:       ['./antrag-document-upload-dialog.component.scss'],
  standalone:      true,
  imports:         [
    ReactiveDialogContentComponent,
    MatDialogActions,
    BasicIconButtonComponent,
    UploadFileComponent,
    FormatNumberPipe
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AntragDocumentUploadDialogComponent extends AbstractReactiveDialogComponent<PolizzeResult, MaklerPolizzeDetails, AntragDocumentUploadDialogContext, never> {

  private dataService = inject(DataService);
  private messageService = inject(MessageService);
  private destroyRef = inject(DestroyRef);

  protected currentDocuments: SimpleDocument[] = [];
  protected uploadFileCallback = this.dataService.uploadFileMaklerDocument.bind(this.dataService);

  constructor() {
    super();
    this.dialogInputData.title = 'Dokumentupload: ' + capitalize(this.maklerDocumentType?.toString());

    if (this.context?.simpleDocument) {
      this.currentDocuments.push(this.context?.simpleDocument);
    }
  }

  submit() {
    this.dataService.putPolizzeMaklerDocument(this.polizzeResult.id, {
        polizzeId:    this.polizzeResult.id,
        fileUpload:   {
          fileId:   this.firstDocument.key,
          fileName: this.firstDocument.name,
          user:     null,
          mimetype: null
        },
        documentType: this.context.maklerDocumentType
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        take(1),
        filter(_payload => this.messageService.handlePayloadReturnSuccess(_payload))
      )
      .subscribe(
        _payload => this.dialogRef.close({
          status: 'success',
          data:   _payload.data
        })
      );
  }

  get isSubmitDisabled() {
    return !this.currentDocuments?.length;
  }

  get polizzeResult() {
    return this.data;
  }

  get maklerDocumentType() {
    return this.context.maklerDocumentType;
  }

  // only 1 document is allowed
  get firstDocument() {
    return this.currentDocuments[0];
  }
}
